import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

interface BillsState {
  bills: any[];
  currentBill: any;
  pagination: {
    total: number;
    page: number;
    per_page: number;
    has_next: boolean;
  };
  loading: boolean;
  error: string | null;
  filters: {
    congress?: number;
    billType?: string;
  };
}

const initialState: BillsState = {
  bills: [],
  currentBill: null,
  pagination: {
    total: 0,
    page: 1,
    per_page: 50,
    has_next: false,
  },
  loading: false,
  error: null,
  filters: {},
};

export const fetchBills = createAsyncThunk(
  'bills/fetchBills',
  async (params: { 
    limit?: number; 
    offset?: number; 
    congress?: number; 
    billType?: string; 
    q?: string;
    view?: string;
  }) => {
    const response = await apiService.getBills(params);
    return response.data;
  }
);

export const fetchBillDetail = createAsyncThunk(
  'bills/fetchBillDetail',
  async (params: { congress: number; billType: string; billNumber: string }) => {
    const response = await apiService.getBillDetail(params.congress, params.billType, params.billNumber);
    return response.data;
  }
);

export const fetchBillActions = createAsyncThunk(
  'bills/fetchBillActions',
  async (params: { congress: number; billType: string; billNumber: string }) => {
    const response = await apiService.getBillActions(params.congress, params.billType, params.billNumber);
    return response.data;
  }
);

const billsSlice = createSlice({
  name: 'bills',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearCurrentBill: (state) => {
      state.currentBill = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBills.fulfilled, (state, action) => {
        state.loading = false;
        state.bills = action.payload.bills;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bills';
      })
      .addCase(fetchBillDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentBill = null;
      })
      .addCase(fetchBillDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBill = action.payload;
      })
      .addCase(fetchBillDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bill detail';
        state.currentBill = null;
      });
  },
});

export const { setFilters, clearCurrentBill, clearError } = billsSlice.actions;
export default billsSlice.reducer;
